import React, { useState } from 'react';
import '../../styles/pages.css';
import '../../styles/settings.css';

const APP_VERSION = '1.0.0';
const DEVELOPER = 'Victrie.com';
const RELEASE_DATE = 'June 2024';
const CURRENT_YEAR = new Date().getFullYear();

export default function Settings() {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [notifications, setNotifications] = useState({
    email: true,
    sms: true,
    marketing: false
  });

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const handleNotificationChange = (type) => {
    setNotifications({
      ...notifications,
      [type]: !notifications[type]
    });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Settings</h1>
        <p>Manage your application settings and preferences</p>
      </div>

      {/* Preferences Section */}
      <div className="page-section">
        <div className="settings-group">
          <h3 className="settings-title">Preferences</h3>

          <div className="settings-section">
            <h4>Notifications</h4>
            <div className="settings-options">
              <label className="setting-checkbox">
                <input
                  type="checkbox"
                  checked={notifications.email}
                  onChange={() => handleNotificationChange('email')}
                />
                <span className="checkbox-label">Email Notifications</span>
                <span className="checkbox-desc">Receive email updates about your account activity</span>
              </label>
              <label className="setting-checkbox">
                <input
                  type="checkbox"
                  checked={notifications.sms}
                  onChange={() => handleNotificationChange('sms')}
                />
                <span className="checkbox-label">SMS Delivery Updates</span>
                <span className="checkbox-desc">Get notified when SMS campaigns are delivered</span>
              </label>
              <label className="setting-checkbox">
                <input
                  type="checkbox"
                  checked={notifications.marketing}
                  onChange={() => handleNotificationChange('marketing')}
                />
                <span className="checkbox-label">Marketing Emails</span>
                <span className="checkbox-desc">Receive tips, updates, and promotional offers</span>
              </label>
            </div>
          </div>

          <div className="settings-section">
            <h4>Theme</h4>
            <div className="theme-options">
              <button
                className={`theme-btn ${theme === 'light' ? 'active' : ''}`}
                onClick={() => handleThemeChange('light')}
              >
                <div className="theme-preview light"></div>
                <span>Light</span>
              </button>
              <button
                className={`theme-btn ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => handleThemeChange('dark')}
              >
                <div className="theme-preview dark"></div>
                <span>Dark</span>
              </button>
              <button
                className={`theme-btn ${theme === 'auto' ? 'active' : ''}`}
                onClick={() => handleThemeChange('auto')}
              >
                <div className="theme-preview auto"></div>
                <span>Auto</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* System Information */}
      <div className="page-section">
        <div className="settings-group">
          <h3 className="settings-title">System Information</h3>

          <div className="info-grid">
            <div className="info-card">
              <div className="info-header">
                <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="1"/>
                  <path d="M12 1v6m0 6v6"/>
                  <path d="M4.22 4.22l4.24 4.24m5.08 5.08l4.24 4.24"/>
                  <path d="M1 12h6m6 0h6"/>
                  <path d="M4.22 19.78l4.24-4.24m5.08-5.08l4.24-4.24"/>
                </svg>
                <span>Application Version</span>
              </div>
              <p className="info-value">{APP_VERSION}</p>
              <p className="info-meta">Latest stable release • {RELEASE_DATE}</p>
            </div>

            <div className="info-card">
              <div className="info-header">
                <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                  <path d="M12.5 7H11v6l5.2 3.2"/>
                </svg>
                <span>Last Updated</span>
              </div>
              <p className="info-value">{RELEASE_DATE}</p>
              <p className="info-meta">Check for updates regularly</p>
            </div>

            <div className="info-card">
              <div className="info-header">
                <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <span>Support</span>
              </div>
              <p className="info-value">24/7 Available</p>
              <p className="info-meta">Contact support team for assistance</p>
            </div>

            <div className="info-card">
              <div className="info-header">
                <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                </svg>
                <span>Status</span>
              </div>
              <p className="info-value">All Systems Operational</p>
              <p className="info-meta">No known issues</p>
            </div>
          </div>
        </div>
      </div>

      {/* Developer Information */}
      <div className="page-section">
        <div className="settings-group">
          <h3 className="settings-title">About</h3>

          <div className="about-section">
            <div className="about-content">
              <h4>Mustech Bulk SMS</h4>
              <p>A comprehensive SMS management platform designed to help businesses reach their customers instantly and efficiently.</p>

              <div className="about-meta">
                <div className="meta-item">
                  <span className="meta-label">Developed & Maintained by:</span>
                  <span className="meta-value">
                    <a href="https://victrie.com" target="_blank" rel="noopener noreferrer">
                      Victrie.com
                    </a>
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Version:</span>
                  <span className="meta-value">{APP_VERSION}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Released:</span>
                  <span className="meta-value">{RELEASE_DATE}</span>
                </div>
              </div>

              <div className="about-links">
                <a href="#privacy" className="about-link">Privacy Policy</a>
                <span className="link-separator">•</span>
                <a href="#terms" className="about-link">Terms of Service</a>
                <span className="link-separator">•</span>
                <a href="#contact" className="about-link">Contact Support</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer/Copyright */}
      <div className="copyright-section">
        <p>&copy; {CURRENT_YEAR} {DEVELOPER}. All rights reserved.</p>
        <p>Mustech Bulk SMS is a product of {DEVELOPER}</p>
        <div className="version-info">
          <span>v{APP_VERSION}</span>
          <span className="dot">•</span>
          <span>Powered by Mustech Platform</span>
        </div>
      </div>
    </div>
  );
}
